import type {
  IMenuDraft,
  IMenuImportTurn,
  IMenuProposal,
  TMenuProposalStatus,
} from '@/interfaces/IMenuImport';
import {
  isCategoryRecord,
  isItemRecord,
  proposalLines,
} from './proposal-effects';
import type { IEffectLine, TProposalFormat } from './proposal-effects';

/**
 * «Pedir cambios» (plan §4.6): what the assistant panel shows around the
 * chat turns (composer availability, the «Sobre: …» chip, the proposal
 * cards and where «Ver cambios» takes the list). The components only
 * render these.
 */

/** Longest chat message (§4.6). */
export const CHAT_MESSAGE_MAX = 500;
/** Change rows a proposal card lists before «y {count} más». */
export const PROPOSAL_LINES_MAX = 20;

export type TAssistantMode = 'ready' | 'aiOff' | 'reading';

/** The composer, or the line that replaces it (AI off wins: it does not end with the read). */
export function assistantMode(input: {
  aiEnabled: boolean;
  reading: boolean;
}): TAssistantMode {
  if (!input.aiEnabled) return 'aiOff';
  return input.reading ? 'reading' : 'ready';
}

/** The message to send: trimmed and at most 500 characters; null when empty. */
export function chatMessage(text: string): string | null {
  const message = text.trim().slice(0, CHAT_MESSAGE_MAX).trim();
  return message.length > 0 ? message : null;
}

/** The parts of a key press the composer reads (no DOM types in the domain). */
export interface IComposerKey {
  key: string;
  shiftKey: boolean;
  /** An input method (e.g. Japanese) is composing: Enter confirms the text. */
  isComposing: boolean;
}

/** Enter sends, Shift+Enter adds a line (the textarea's own behaviour). */
export function isSendKey(event: IComposerKey): boolean {
  return event.key === 'Enter' && !event.shiftKey && !event.isComposing;
}

export interface IChatSelection {
  itemId: string;
  name: string;
}

/**
 * «Sobre: {name}»: the dish whose editor is open, unless the owner removed
 * the chip for it. The turn then carries that dish as its selection.
 */
export function chatSelection(
  draft: IMenuDraft | null,
  openItemId: string | null,
  clearedItemId: string | null
): IChatSelection | null {
  if (!draft || openItemId === null || openItemId === clearedItemId)
    return null;
  const item = draft.items[openItemId];
  return item ? { itemId: item.id, name: item.name } : null;
}

/** Changes the chat log follows to its end: a new turn, or the last one getting its answer. */
export function chatLogKey(turns: IMenuImportTurn[]): string {
  const last = turns[turns.length - 1];
  return last ? `${turns.length}:${last.turnId}:${last.status}` : '0';
}

export interface IProposalCardModel {
  /** «Propuesta · {count} cambios» while pending; null once applied, discarded or stale. */
  count: number | null;
  /** At most `PROPOSAL_LINES_MAX` change rows. */
  lines: IEffectLine[];
  /** «y {count} más». */
  more: number;
}

/**
 * The count and change rows of a proposal card (the effects the owner would
 * notice). Only a pending proposal carries its effects (§4.6, §5.4): an
 * applied, discarded or stale card shows its summary and state, no count and
 * no rows.
 */
export function proposalCardModel(
  proposal: IMenuProposal,
  draft: IMenuDraft,
  fmt: TProposalFormat
): IProposalCardModel {
  if (proposal.status !== 'pending') return { count: null, lines: [], more: 0 };
  const lines = proposalLines(proposal, draft, fmt);
  return {
    count: lines.length > 0 ? lines.length : proposal.effects.length,
    lines: lines.slice(0, PROPOSAL_LINES_MAX),
    more: Math.max(0, lines.length - PROPOSAL_LINES_MAX),
  };
}

export interface IProposalActions {
  apply: boolean;
  discard: boolean;
  retry: boolean;
}

/** Pending: «Aplicar», «Descartar», «Probar otra vez»; stale: only «Probar otra vez»; applied or discarded: none. */
export function proposalActions(status: TMenuProposalStatus): IProposalActions {
  switch (status) {
    case 'pending':
      return { apply: true, discard: true, retry: true };
    case 'stale':
      return { apply: false, discard: false, retry: true };
    default:
      return { apply: false, discard: false, retry: false };
  }
}

/** Where «Ver cambios» scrolls the list: a dish row, else its category's header. */
export interface IListReveal {
  itemId: string | null;
  categoryId: string | null;
}

/**
 * The first row of the list a proposal marks (effect order): a dish it
 * changes or removes, else the category a dish it adds goes to, else a
 * category it renames, moves or removes. Null when it only touches add-on
 * groups or new categories (not in the list yet).
 */
export function proposalRevealTarget(
  proposal: IMenuProposal,
  draft: IMenuDraft
): IListReveal | null {
  for (const effect of proposal.effects) {
    if (effect.entity === 'item') {
      const current = draft.items[effect.id];
      if (effect.change !== 'add' && current)
        return { itemId: current.id, categoryId: current.categoryId };
      if (
        effect.change === 'add' &&
        isItemRecord(effect.after) &&
        draft.categories[effect.after.categoryId]
      )
        return { itemId: null, categoryId: effect.after.categoryId };
    } else if (effect.entity === 'category') {
      const record = effect.before ?? effect.after;
      if (
        effect.change !== 'add' &&
        isCategoryRecord(record) &&
        draft.categories[effect.id]
      )
        return { itemId: null, categoryId: effect.id };
    }
  }
  return null;
}
